import type { SaveGame } from "../types";
import { LocalPlatformAdapter, type AdEvent, type PlatformCapabilities } from "./PlatformAdapter";

const SAVE_KEY = "save";
const LEADERBOARD_NAME = "main";

type AdCallbacks = {
  onOpen?: () => void;
  onClose?: (wasShown: boolean) => void;
  onRewarded?: () => void;
  onError?: (error: unknown) => void;
};

interface YandexPlayer {
  setData(data: Record<string, unknown>, flush?: boolean): Promise<void>;
  getData(keys?: string[]): Promise<Record<string, unknown>>;
}

interface YandexLeaderboards {
  setLeaderboardScore(name: string, score: number): Promise<void>;
}

interface YandexSdk {
  features?: {
    LoadingAPI?: { ready?: () => void };
    GameplayAPI?: { start?: () => void; stop?: () => void };
  };
  adv: {
    showFullscreenAdv(opts: { callbacks: AdCallbacks }): void;
    showRewardedVideo(opts: { callbacks: AdCallbacks }): void;
  };
  getPlayer(): Promise<YandexPlayer>;
  getLeaderboards(): Promise<YandexLeaderboards>;
  environment?: { i18n?: { lang?: string } };
}

interface YaGamesGlobal {
  init(opts?: Record<string, unknown>): Promise<YandexSdk>;
}

function yaGames(): YaGamesGlobal | undefined {
  return (globalThis as unknown as { YaGames?: YaGamesGlobal }).YaGames;
}

/**
 * Yandex Games adapter over the YaGames SDK (loaded as an external /sdk.js in the
 * yandex build). Every SDK touch is feature-detected and wrapped: if the SDK is
 * absent (e.g. running the build outside Yandex) the adapter degrades to the
 * no-op LocalPlatformAdapter behaviour instead of throwing.
 */
export class YandexPlatformAdapter extends LocalPlatformAdapter {
  private ysdk: YandexSdk | null = null;
  private player: YandexPlayer | null = null;
  private readonly adHandlers: ((event: AdEvent) => void)[] = [];

  getCapabilities(): PlatformCapabilities {
    return { ads: true, rewarded: true, banner: true, cloudSave: true, leaderboard: true };
  }

  async init(): Promise<void> {
    const sdk = yaGames();
    if (!sdk?.init) {
      return;
    }
    this.ysdk = await sdk.init();
    try {
      this.player = await this.ysdk.getPlayer();
    } catch {
      this.player = null;
    }
  }

  /** SDK locale (ru/en/...) for language auto-detection; null when SDK is absent. */
  getDetectedLanguage(): string | null {
    return this.ysdk?.environment?.i18n?.lang ?? null;
  }

  gameReady(): void {
    this.ysdk?.features?.LoadingAPI?.ready?.();
  }

  notifyLoaded(): void {
    this.ysdk?.features?.LoadingAPI?.ready?.();
  }

  gameplayStart(): void {
    this.ysdk?.features?.GameplayAPI?.start?.();
  }

  gameplayStop(): void {
    this.ysdk?.features?.GameplayAPI?.stop?.();
  }

  onAdEvent(handler: (event: AdEvent) => void): void {
    this.adHandlers.push(handler);
  }

  private emit(event: AdEvent): void {
    for (const handler of this.adHandlers) {
      handler(event);
    }
  }

  async showInterstitialAd(): Promise<void> {
    const sdk = this.ysdk;
    if (!sdk) {
      return;
    }
    await new Promise<void>((resolve) => {
      let settled = false;
      const done = (): void => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };
      sdk.adv.showFullscreenAdv({
        callbacks: {
          onOpen: () => this.emit({ type: "adStarted" }),
          onClose: () => { this.emit({ type: "adFinished" }); done(); },
          onError: (error) => { this.emit({ type: "adError", message: String(error) }); done(); },
        },
      });
    });
  }

  async showRewardedAd(): Promise<boolean> {
    const sdk = this.ysdk;
    if (!sdk) {
      return false;
    }
    return new Promise<boolean>((resolve) => {
      let rewarded = false;
      let settled = false;
      const settle = (value: boolean): void => {
        if (!settled) {
          settled = true;
          resolve(value);
        }
      };
      sdk.adv.showRewardedVideo({
        callbacks: {
          onOpen: () => this.emit({ type: "adStarted", rewarded: true }),
          onRewarded: () => { rewarded = true; },
          onClose: () => { this.emit({ type: "adFinished", rewarded: true }); settle(rewarded); },
          onError: (error) => { this.emit({ type: "adError", rewarded: true, message: String(error) }); settle(false); },
        },
      });
    });
  }

  async saveProgress(state: SaveGame): Promise<void> {
    if (!this.player) {
      return;
    }
    try {
      await this.player.setData({ [SAVE_KEY]: state }, true);
    } catch {
      // cloud save is best-effort; localStorage persist is the source of truth
    }
  }

  async loadProgress(): Promise<SaveGame | null> {
    if (!this.player) {
      return null;
    }
    try {
      const data = await this.player.getData([SAVE_KEY]);
      return (data[SAVE_KEY] as SaveGame | undefined) ?? null;
    } catch {
      return null;
    }
  }

  async submitLeaderboard(score: number): Promise<void> {
    const sdk = this.ysdk;
    if (!sdk) {
      return;
    }
    try {
      const leaderboards = await sdk.getLeaderboards();
      await leaderboards.setLeaderboardScore(LEADERBOARD_NAME, score);
    } catch {
      // leaderboard is optional; never break the game
    }
  }
}
